import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  skip,
  Subscription,
  switchMap,
  takeWhile,
} from 'rxjs';
import { Users } from 'server/shared/collections/users';
import { Customer } from 'server/shared/models/customer';
import { RoleEnum } from 'server/shared/models/role.enum';
import {
  DispoUser,
  PoolerUser,
  RequesterUser,
} from 'server/shared/models/user';
import { rxDistinctJSONStringify } from 'src/helpers/rxDistinctJSONStringify';
import { rxDistinctStringArrayChanged } from 'src/helpers/rxDistinctStringArray';
import { ManagedSubscription } from './managed-subscription';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  usersLimit$ = new BehaviorSubject<number | undefined>(200);
  /**
   * Managed Subscriptions
   */
  settings = new ManagedSubscription('settings');
  sharedPoolCustomers = new ManagedSubscription('sharedPoolCustomers');
  sharedPoolTargetCustomers = new ManagedSubscription(
    'sharedPoolTargetCustomers',
  );
  adminCustomers = new ManagedSubscription('adminCustomers');
  transferCustomer = new ManagedSubscription('transferCustomers');
  requestedUsers = new ManagedSubscription('requestedUsers');
  createdUsers = new ManagedSubscription('createdUsers');
  documentUploads = new ManagedSubscription('documentUploads');
  organisations = new ManagedSubscription('organisations');
  organisationOwnerCustomers = new ManagedSubscription(
    'organisationOwnerCustomers',
  );
  possibleEmployments = new ManagedSubscription('possibleEmployments');
  poolerEmployments = new ManagedSubscription('poolerEmploymentsList');
  previousJobs = new ManagedSubscription('previous-jobs-of-customer');
  certificates = new ManagedSubscription('certificates');
  specialities = new ManagedSubscription('specialities');
  pendingTimetrackingJobs = new ManagedSubscription('pendingTimetrackingJobs');
  organisationRequests = new ManagedSubscription('organisationRequests');
  myProfile = new ManagedSubscription('myProfile');
  myCustomer = new ManagedSubscription('myCustomer');
  jobs = new ManagedSubscription('jobs');
  private transcribeJobsSubscription: ManagedSubscription | undefined =
    undefined;
  private activeTranscribeSubscriptionOptions$ = new BehaviorSubject<
    { publication: string; queryParams: Record<string, unknown> } | undefined
  >(undefined);

  transcribeJobsLoading$ = this.activeTranscribeSubscriptionOptions$.pipe(
    rxDistinctJSONStringify(),
    switchMap((options) => {
      if (!options) {
        this.transcribeJobsSubscription?.unsubscribe();
        this.transcribeJobsSubscription = undefined;
        return of(false);
      }
      this.transcribeJobsSubscription?.unsubscribe();
      this.transcribeJobsSubscription = new ManagedSubscription(
        options.publication,
        options.queryParams,
      );
      this.transcribeJobsSubscription.subscribe();
      return this.transcribeJobsSubscription.loading$;
    }),
    shareReplay(1),
  );
  employments = new ManagedSubscription('employmentsList');

  actionRequiredUsersCount = new ManagedSubscription(
    'actionRequiredUsersCount',
  );
  actionRequiredJobsCount = new ManagedSubscription('actionRequiredJobsCount');
  actionRequiredEmploymentsCount = new ManagedSubscription(
    'actionRequiredEmploymentsCount',
  );
  jobRequestsCount = new ManagedSubscription('jobRequestsCount');

  dispoUsersOfCustomer = new ManagedSubscription('dispoUsersOfCustomer');
  requesterUsersOfCustomer = new ManagedSubscription(
    'requesterUsersOfCustomer',
  );
  poolerUsersOfCustomer = new ManagedSubscription('poolerUsersOfCustomer');

  ownUsersWithEmploymentAvailabilities = new ManagedSubscription(
    'ownUsersWithEmploymentAvailabilities',
  );
  sharedUsersWithEmploymentAvailabilities = new ManagedSubscription(
    'sharedUsersWithEmploymentAvailabilities',
  );

  ownUsersWithJobAvailabilities = new ManagedSubscription(
    'ownUsersWithJobAvailabilities',
  );
  sharedUsersWithJobAvailabilities = new ManagedSubscription(
    'sharedUsersWithJobAvailabilities',
  );

  private archivedJobs: ManagedSubscription | undefined = undefined;
  private archivedJobsOptions$ = new BehaviorSubject<any>(undefined);

  private archivedEmployments: ManagedSubscription | undefined = undefined;
  private archivedEmploymentsOptions$ = new BehaviorSubject<any>(undefined);

  archivedJobsLoading$ = this.archivedJobsOptions$.pipe(
    rxDistinctJSONStringify(),
    switchMap((options) => {
      if (!options) {
        return of(true);
      }
      this.archivedJobs?.unsubscribe();
      this.archivedJobs = new ManagedSubscription('archived-jobs', options);
      return this.archivedJobs.loading$;
    }),
    shareReplay(1),
  );

  archivedEmploymentsLoading$ = this.archivedEmploymentsOptions$.pipe(
    distinctUntilChanged(
      (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b),
    ),
    switchMap((options) => {
      if (!options) {
        return of(true);
      }
      this.archivedEmployments?.unsubscribe();
      this.archivedEmployments = new ManagedSubscription(
        'archivedEmploymentsList',
        options,
      );
      return this.archivedEmployments.loading$;
    }),
    shareReplay(1),
  );

  private jobMatrixJobs: ManagedSubscription | undefined = undefined;
  private jobMatrixJobsOptions$ = new BehaviorSubject<any>(undefined);

  jobMatrixJobsLoading$ = this.jobMatrixJobsOptions$.pipe(
    distinctUntilChanged(
      (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b),
    ),
    switchMap((options) => {
      if (!options) {
        // Unsubscribe when options is undefined
        this.jobMatrixJobs?.unsubscribe();
        this.jobMatrixJobs = undefined;
        return of(false);
      }
      this.jobMatrixJobs?.unsubscribe();
      this.jobMatrixJobs = new ManagedSubscription('jobMatrixJobs', options);
      return this.jobMatrixJobs.loading$;
    }),
    shareReplay(1),
  );

  // Aggregated request overview jobs subscription
  private aggregatedJobMatrixJobs: ManagedSubscription | undefined = undefined;
  private aggregatedJobMatrixJobsOptions$ = new BehaviorSubject<any>(undefined);

  aggregatedJobMatrixJobsLoading$ = this.aggregatedJobMatrixJobsOptions$.pipe(
    distinctUntilChanged(
      (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b),
    ),
    switchMap((options) => {
      if (!options) {
        return of(true);
      }
      this.aggregatedJobMatrixJobs?.unsubscribe();
      this.aggregatedJobMatrixJobs = new ManagedSubscription(
        'aggregatedJobMatrixJobs',
        options,
      );
      return this.aggregatedJobMatrixJobs.loading$;
    }),
    shareReplay(1),
  );

  /**
   * Managed Ready states
   */
  usersOfCustomerReady$ = combineLatest([
    this.dispoUsersOfCustomer.ready$,
    this.poolerUsersOfCustomer.ready$,
    this.requesterUsersOfCustomer.ready$,
  ]).pipe(map(([dispo, pooler, requester]) => dispo && pooler && requester));

  subscribeEmploymentUsers() {
    this.ownUsersWithEmploymentAvailabilities.subscribe();
    this.sharedUsersWithEmploymentAvailabilities.subscribe();
  }
  employmentUsersReady$ = combineLatest([
    this.ownUsersWithEmploymentAvailabilities.ready$,
    this.sharedUsersWithEmploymentAvailabilities.ready$,
  ]).pipe(map(([own, shared]) => own && shared));

  subscribeJobUsers() {
    this.ownUsersWithJobAvailabilities.subscribe();
    this.sharedUsersWithJobAvailabilities.subscribe();
  }
  jobUsersReady$ = combineLatest([
    this.ownUsersWithJobAvailabilities.ready$,
    this.sharedUsersWithJobAvailabilities.ready$,
  ]).pipe(map(([own, shared]) => own && shared));

  // subject updated by customer service
  // dispo OR requester customer
  dispoCustomerSubject$ = new BehaviorSubject<Customer | undefined>(undefined);
  readyDispoCustomer$ = this.dispoCustomerSubject$.pipe(
    filter((customer): customer is Customer => !!customer),
    shareReplay(1),
  );

  dispoUsersOfCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [
      ...(customer.dispoIds || []),
      ...(customer.createdUserIds || []),
    ]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  poolerUsersOfCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [
      ...customer.pool.activeUserIds,
      ...customer.pool.registeredUserIds,
      ...(customer.pool.formerUserIds || []),
    ]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  requesterUsersOfCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [...(customer.requesterIds || [])]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  requestedUsersOfCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [...customer.pool.requestedUserIds]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  createdUsersOfCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [...(customer.createdUserIds || [])]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  transferCustomersChanged$ = this.readyDispoCustomer$.pipe(
    map((customer) => [
      ...(customer.customerTransfers?.transfers?.map(
        (transfer) => transfer.targetCustomer.id,
      ) || []),
      ...(customer.customerTransfers?.employmentTransfers?.map(
        (transfer) => transfer.targetCustomer.id,
      ) || []),
    ]),
    rxDistinctStringArrayChanged(),
    skip(1),
  );

  subscribe() {
    this.settings.subscribe();
    this.dispoUsersOfCustomersChanged$.subscribe(() => {
      this.dispoUsersOfCustomer.subscribe(true);
    });
    this.poolerUsersOfCustomersChanged$.subscribe(() => {
      if (this.poolerUsersOfCustomer.isSubscribed) {
        this.poolerUsersOfCustomer.subscribe(true);
      }
    });
    this.requesterUsersOfCustomersChanged$.subscribe(() => {
      if (this.poolerUsersOfCustomer.isSubscribed) {
        this.requesterUsersOfCustomer.subscribe(true);
      }
    });
    this.requestedUsersOfCustomersChanged$.subscribe(() => {
      this.requestedUsers.subscribe(true);
    });
    this.createdUsersOfCustomersChanged$.subscribe(() => {
      this.createdUsers.subscribe(true);
    });
    this.transferCustomersChanged$.subscribe(() => {
      this.transferCustomer.subscribe(true);
    });
    // Subscribe to myProfile and set up observers
    this.myProfile.ready$.subscribe(() => {
      let user = Users.findOne(Meteor.userId());
      if (user && user.role == RoleEnum.DISPO) {
        if (user.isAdmin) {
          this.adminCustomers.subscribe();
        }
        // subscribe dispos initialy because they are used for user-selects in filters
        this.sharedPoolCustomers.subscribe();
        this.sharedPoolTargetCustomers.subscribe();
        this.requestedUsers.subscribe();
        this.createdUsers.subscribe();
        this.transferCustomer.subscribe();
        this.previousJobs.subscribe();
        this.organisationOwnerCustomers.subscribe();
      } else if (user && user.role == RoleEnum.REQUESTER) {
        this.organisationOwnerCustomers.subscribe();
      }
    });

    this.dispoUsersOfCustomer.subscribe();
    this.myCustomer.subscribe();

    // Subscribe to documentUploads
    this.documentUploads.subscribe();
  }

  subscribeArchivedEmployments(options: any) {
    this.archivedEmploymentsOptions$.next(options);
  }

  subscribeArchivedJobs(options: any) {
    this.archivedJobsOptions$.next(options);
  }

  setTranscribeJobsSubscription(
    publication:
      | 'openTranscribeJobs'
      | 'transcribeJobs'
      | 'transcribeJobsByDate',
    queryParams: Record<string, unknown> = {},
  ) {
    this.activeTranscribeSubscriptionOptions$.next({
      publication,
      queryParams,
    });
  }

  clearTranscribeJobsSubscription() {
    this.activeTranscribeSubscriptionOptions$.next(undefined);
  }

  archivedJobsLoadingSubscription: Subscription | undefined = undefined;
  initPersistentArchivedJobsSubscription() {
    if (this.archivedJobsLoadingSubscription) {
      return;
    }
    this.archivedJobsLoadingSubscription =
      this.archivedJobsLoading$.subscribe();
  }

  jobsLoadingSubscription: Subscription | undefined = undefined;
  initPersistentJobsSubscription() {
    if (this.jobsLoadingSubscription) {
      return;
    }
    this.jobsLoadingSubscription = this.jobs.loading$.subscribe();
  }

  subscribeJobMatrixJobs(options: any) {
    this.jobMatrixJobsOptions$.next(options);
  }

  subscribeAggregatedJobMatrixJobs(options: any) {
    this.aggregatedJobMatrixJobsOptions$.next(options);
  }

  async getReadyUser(): Promise<PoolerUser | DispoUser | RequesterUser> {
    return new Promise((resolve) => {
      this.myProfile.ready$
        .pipe(takeWhile((ready) => !ready, true)) // only subscribes until ready
        .subscribe((ready) => {
          if (ready) {
            resolve(Users.findOne(Meteor.userId()));
          }
        });
    });
  }

  async getReadyDispoUser(): Promise<DispoUser> {
    return new Promise((resolve) => {
      this.myProfile.ready$
        .pipe(takeWhile((ready) => !ready, true)) // only subscribes until ready
        .subscribe((ready) => {
          if (ready) {
            resolve(
              Users.findOne({
                _id: Meteor.userId(),
                role: RoleEnum.DISPO,
              }) as DispoUser,
            );
          }
        });
    });
  }

  unsubscribe() {
    this.settings.unsubscribe();
    this.jobs.unsubscribe();
    this.employments.unsubscribe();
    this.actionRequiredUsersCount.unsubscribe();
    this.actionRequiredJobsCount.unsubscribe();
    this.actionRequiredEmploymentsCount.unsubscribe();
    this.jobRequestsCount.unsubscribe();
    this.dispoUsersOfCustomer.unsubscribe();
    this.requesterUsersOfCustomer.unsubscribe();
    this.poolerUsersOfCustomer.unsubscribe();
    this.ownUsersWithEmploymentAvailabilities.unsubscribe();
    this.sharedUsersWithEmploymentAvailabilities.unsubscribe();
    this.ownUsersWithJobAvailabilities.unsubscribe();
    this.sharedUsersWithJobAvailabilities.unsubscribe();
    this.myProfile.unsubscribe();
    this.myCustomer.unsubscribe();
    this.requestedUsers.unsubscribe();
    this.documentUploads.unsubscribe();
    this.organisations.unsubscribe();
    this.organisationOwnerCustomers.unsubscribe();
    this.certificates.unsubscribe();
    this.specialities.unsubscribe();
    this.pendingTimetrackingJobs.unsubscribe();
    this.sharedPoolCustomers.unsubscribe();
    this.sharedPoolTargetCustomers.unsubscribe();
    this.adminCustomers.unsubscribe();
    this.transferCustomer.unsubscribe();
    this.createdUsers.unsubscribe();
    this.previousJobs.unsubscribe();
    this.possibleEmployments.unsubscribe();
    this.poolerEmployments.unsubscribe();
    this.organisationRequests.unsubscribe();
    this.archivedJobsOptions$.next(undefined);
    this.archivedJobs?.unsubscribe();
    this.clearTranscribeJobsSubscription();
    this.archivedEmploymentsOptions$.next(undefined);
    this.archivedEmployments?.unsubscribe();
    this.jobMatrixJobsOptions$.next(undefined);
    this.jobMatrixJobs?.unsubscribe();
    this.aggregatedJobMatrixJobsOptions$.next(undefined);
    this.aggregatedJobMatrixJobs?.unsubscribe();
  }
}
